import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Gamepad2, Link as LinkIcon, CreditCard, ShieldCheck, Coins, TrendingUp, Loader2 } from 'lucide-react';
import { push, ref, set, runTransaction } from 'firebase/database';
import { useSelector, useDispatch } from 'react-redux';
import { database, addPointsHistory } from '../../services/firebase';
import { DB_PATHS } from '../../services/schema';
import { setUser } from '../../store/slices/authSlice';
import toastManager from '../../utils/toastManager';
import CheckoutModal from '../checkout/CheckoutModal';

const FALLBACK_FEE = 5;
const MAX_OFFER_AMOUNT = 500;

const RequestScriptModal = ({ isOpen, onClose, onPaymentStarted }) => {
  const dispatch = useDispatch();
  const { settings } = useSelector((state) => state.settings);
  const { user } = useSelector((state) => state.auth);

  // Two separate amounts: the fee is charged now to submit; the offer is a pledge the user
  // only pays later if the script actually gets built.
  const requestFee = Math.max(0, Number(settings?.scriptRequestFee) || FALLBACK_FEE);
  const minOffer = Math.max(0, Number(settings?.scriptRequestMinAmount) || FALLBACK_FEE);
  const pointsRate = Number(settings?.pointsRate) || 20;
  const availablePoints = Number(user?.rewardPoints) || 0;

  const [loading, setLoading] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutItem, setCheckoutItem] = useState(null);
  const [payWithPoints, setPayWithPoints] = useState(false);
  const [formData, setFormData] = useState({
    gameName: '',
    storeLink: '',
    note: '',
    offerAmount: ''
  });

  const offerAmount = Number(formData.offerAmount) || 0;
  // Points only ever pay the submission fee, never the pledged offer.
  const pointsRequired = useMemo(() => Math.ceil(requestFee * pointsRate), [requestFee, pointsRate]);
  const hasEnoughPoints = availablePoints >= pointsRequired;

  const resetForm = () => {
    setFormData({ gameName: '', storeLink: '', note: '', offerAmount: '' });
    setPayWithPoints(false);
  };

  const closeAll = () => {
    setCheckoutOpen(false);
    setCheckoutItem(null);
    resetForm();
    onClose?.();
  };

  const handleChange = (event) => {
    setFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const validate = () => {
    if (!formData.gameName.trim()) {
      toastManager.error('Please enter the game name');
      return false;
    }
    if (!String(formData.offerAmount).trim()) {
      toastManager.error('Please enter how much you are offering');
      return false;
    }
    if (!Number.isFinite(offerAmount) || offerAmount < minOffer) {
      toastManager.error(`Minimum offer is $${minOffer.toFixed(2)}`);
      return false;
    }
    if (offerAmount > MAX_OFFER_AMOUNT) {
      toastManager.error(`Maximum offer is $${MAX_OFFER_AMOUNT}`);
      return false;
    }
    return true;
  };

  const buildRequestBase = (requestId) => ({
    gameName: formData.gameName.trim(),
    storeLink: formData.storeLink.trim(),
    note: formData.note.trim(),
    requestFee,
    offerAmount,
    userId: user?.uid || '',
    userEmail: user?.email || '',
    userName: user?.displayName || user?.name || 'Anonymous',
    requestId
  });

  // Points path writes the request directly; no payment proof is needed because the
  // deduction is the payment. Cash path defers to CheckoutModal as before.
  const submitWithPoints = async (requestId) => {
    if (!user?.uid) {
      toastManager.error('Please sign in to continue');
      return;
    }
    if (!hasEnoughPoints) {
      toastManager.error(`You need ${pointsRequired} points but have ${availablePoints}`);
      return;
    }

    setLoading(true);
    try {
      const pointsRef = ref(database, `${DB_PATHS.user(user.uid)}/Reward Points`);
      const result = await runTransaction(pointsRef, (current) => {
        const balance = Number(current) || 0;
        if (balance < pointsRequired) return; // abort
        return balance - pointsRequired;
      });

      if (!result.committed) {
        toastManager.error('Insufficient reward points');
        return;
      }

      const now = new Date().toISOString();
      const record = {
        ...buildRequestBase(requestId),
        status: 'pending',
        paymentStatus: 'accepted',
        paymentMethod: 'Reward Points',
        paymentCategory: 'points',
        paymentProofType: 'points',
        paymentProof: `${pointsRequired} pts`,
        pointsSpent: pointsRequired,
        paidWithPoints: true,
        Payments: {
          Amount: requestFee,
          'Pay method': 'Reward Points',
          'Transaction date': now,
          'Transaction id': `POINTS-${pointsRequired}`
        },
        createdAt: now,
        updatedAt: now
      };

      await set(ref(database, `${DB_PATHS.SCRIPT_REQUESTS}/${requestId}`), record);
      await addPointsHistory(user.uid, pointsRequired, 'debit', `Script request: ${record.gameName}`);
      dispatch(setUser({ ...user, rewardPoints: Math.max(0, availablePoints - pointsRequired) }));

      toastManager.success(`Request submitted using ${pointsRequired} points`);
      closeAll();
    } catch (error) {
      console.error('Points-based script request failed:', error);
      toastManager.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    const requestId = push(ref(database, DB_PATHS.SCRIPT_REQUESTS)).key;

    if (payWithPoints) {
      submitWithPoints(requestId);
      return;
    }

    setLoading(true);
    try {
      const base = buildRequestBase(requestId);
      const paymentItem = {
        id: `script-request-${requestId}`,
        name: `Script Request: ${base.gameName}`,
        price: requestFee,
        image: '',
        category: 'script-request',
        itemType: 'script-request',
        quantity: 1,
        validity: null,
        ...base
      };

      setCheckoutItem(paymentItem);
      setCheckoutOpen(true);
      onPaymentStarted?.(paymentItem);
      toastManager.success(`Complete the $${requestFee.toFixed(2)} fee payment to submit your request.`);
    } catch (error) {
      console.error('Unable to start script request payment:', error);
      toastManager.error('Failed to start payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && !checkoutOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeAll}
            className="absolute inset-0 bg-gray-950/75 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-950"
          >
            <div className="relative shrink-0 border-b border-gray-200 bg-white px-4 py-3.5 text-gray-950 dark:border-white/10 dark:bg-gray-950 dark:text-white sm:px-5 sm:py-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary-50 text-primary-700 dark:bg-white/[0.08] dark:text-white sm:h-11 sm:w-11">
                    <Gamepad2 className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-black leading-tight sm:text-xl">Request a Script</h3>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      Submitted after the fee is paid
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeAll}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-500 transition hover:bg-gray-200 hover:text-gray-950 dark:bg-white/[0.08] dark:text-gray-300 dark:hover:bg-white/[0.14] dark:hover:text-white"
                  aria-label="Close request form"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-3">
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Game Name <span className="text-red-500">*</span>
                    </span>
                    <span className="relative block">
                      <Gamepad2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        name="gameName"
                        value={formData.gameName}
                        onChange={handleChange}
                        placeholder="e.g. Time Master"
                        className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-3 text-sm font-semibold text-gray-950 outline-none transition focus:border-primary-500 focus:bg-white focus:ring-2 focus:ring-primary-500/20 dark:border-gray-800 dark:bg-gray-900 dark:text-white dark:focus:border-primary-500"
                        required
                      />
                    </span>
                  </label>

                  <label className="block">
                    <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Play Store Link</span>
                    <span className="relative block">
                      <LinkIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="url"
                        name="storeLink"
                        value={formData.storeLink}
                        onChange={handleChange}
                        placeholder="https://play.google.com/store/..."
                        className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-3 text-sm font-semibold text-gray-950 outline-none transition focus:border-primary-500 focus:bg-white focus:ring-2 focus:ring-primary-500/20 dark:border-gray-800 dark:bg-gray-900 dark:text-white dark:focus:border-primary-500"
                      />
                    </span>
                  </label>

                  <label className="block">
                    <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Features / Notes</span>
                    <textarea
                      name="note"
                      value={formData.note}
                      onChange={handleChange}
                      placeholder="Describe features, game version, or any useful detail."
                      rows={3}
                      className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm font-semibold text-gray-950 outline-none transition focus:border-primary-500 focus:bg-white focus:ring-2 focus:ring-primary-500/20 dark:border-gray-800 dark:bg-gray-900 dark:text-white dark:focus:border-primary-500"
                    />
                  </label>
                </div>

                <div className="h-px bg-gray-100 dark:bg-white/10" />

                <label className="block">
                  <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Your Offer if Built <span className="text-red-500">*</span>
                  </span>
                  <span className="relative block">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-400">$</span>
                    <input
                      type="number"
                      name="offerAmount"
                      value={formData.offerAmount}
                      onChange={handleChange}
                      min={minOffer}
                      max={MAX_OFFER_AMOUNT}
                      step="0.5"
                      placeholder={String(minOffer)}
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-8 pr-3 text-sm font-semibold text-gray-950 outline-none transition focus:border-primary-500 focus:bg-white focus:ring-2 focus:ring-primary-500/20 dark:border-gray-800 dark:bg-gray-900 dark:text-white dark:focus:border-primary-500"
                      required
                    />
                  </span>
                  <span className="mt-1.5 flex items-start gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
                    <TrendingUp className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    Higher offer = higher and faster chance of the script being built.
                  </span>
                  <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                    Min ${minOffer.toFixed(2)}. Only charged if the script gets built for you.
                  </span>
                </label>

                <div>
                  <div className="mb-1.5 flex items-baseline justify-between gap-2">
                    <span className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Pay Fee Now</span>
                    <span className="text-sm font-black text-gray-950 dark:text-white">${requestFee.toFixed(2)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPayWithPoints(false)}
                      className={`flex min-h-10 items-center justify-center gap-2 rounded-xl border text-sm font-bold transition ${
                        !payWithPoints
                          ? 'border-primary-500 bg-primary-50 text-primary-700 dark:border-primary-500 dark:bg-primary-500/10 dark:text-primary-200'
                          : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300'
                      }`}
                    >
                      <CreditCard className="h-4 w-4" />
                      Cash
                    </button>
                    <button
                      type="button"
                      onClick={() => setPayWithPoints(true)}
                      className={`flex min-h-10 items-center justify-center gap-2 rounded-xl border text-sm font-bold transition ${
                        payWithPoints
                          ? 'border-amber-500 bg-amber-50 text-amber-700 dark:border-amber-500 dark:bg-amber-500/10 dark:text-amber-200'
                          : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300'
                      }`}
                    >
                      <Coins className="h-4 w-4" />
                      {pointsRequired} pts
                    </button>
                  </div>

                  {payWithPoints && (
                    <p className={`mt-2 text-xs font-semibold ${
                      hasEnoughPoints ? 'text-gray-500 dark:text-gray-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      {hasEnoughPoints
                        ? `Balance: ${availablePoints} pts → ${availablePoints - pointsRequired} pts after`
                        : `Need ${pointsRequired - availablePoints} more pts (balance: ${availablePoints})`}
                    </p>
                  )}
                </div>

                <p className="flex items-start gap-1.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                  <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  The fee comes back as PSH points if your request is denied.
                </p>

                <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={closeAll}
                    className="inline-flex min-h-11 items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-sm font-bold text-gray-700 transition hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !String(formData.offerAmount).trim() || (payWithPoints && !hasEnoughPoints)}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary-600 px-5 text-sm font-black text-white shadow-lg shadow-primary-500/20 transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : payWithPoints ? <Coins className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                    {loading
                      ? 'Submitting...'
                      : payWithPoints
                        ? `Pay ${pointsRequired} pts`
                        : `Pay $${requestFee.toFixed(2)}`}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      <CheckoutModal
        isOpen={checkoutOpen}
        onClose={closeAll}
        directItems={checkoutItem ? [checkoutItem] : []}
        directMode="script-request"
      />
    </AnimatePresence>
  );
};

export default RequestScriptModal;
